import { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Stethoscope, Activity, Heart, User,
  ClipboardCheck, CheckCircle2, Edit3, Clock, MapPin,
  Thermometer, CloudDownload, X, Search, FileText, Download,
  Eye, FolderOpen, FlaskConical, ScanLine, Hospital, ShieldAlert, File,
  Filter, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import { pazientiInCaricoMMG, diarioClinico, paiDaValidare, documentiFSE } from '@/data/mockMMGData';

const tipoIcon = {
  infermiere: { icon: Stethoscope, color: '#06B6D4', label: 'Infermiere' },
  fisioterapista: { icon: Activity, color: '#8B5CF6', label: 'Fisioterapista' },
  oss: { icon: Heart, color: '#F59E0B', label: 'OSS' },
};

/* ══════ DIARIO CLINICO (Timeline) ══════ */
function DiarioTimeline({ eventi }) {
  if (!eventi || !eventi.length) {
    return (
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-muted-foreground">Nessun evento registrato nel diario clinico.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="diario-clinico">
      <CardContent className="p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center">
            <ClipboardCheck className="w-4 h-4 text-cyan-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Diario Clinico Integrato</h2>
            <p className="text-[10px] text-muted-foreground">Report dagli erogatori sul campo (sola lettura)</p>
          </div>
        </div>

        <div className="relative pl-6" data-testid="timeline-events">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border/40" />

          {eventi.map((ev, i) => {
            const cfg = tipoIcon[ev.tipo] || tipoIcon.infermiere;
            const Icon = cfg.icon;
            return (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="relative mb-5 last:mb-0"
                data-testid={`evento-${ev.id}`}
              >
                {/* Dot */}
                <div className="absolute -left-6 top-1 w-[22px] h-[22px] rounded-full bg-card border-2 flex items-center justify-center z-10" style={{ borderColor: cfg.color }}>
                  <Icon className="w-3 h-3" style={{ color: cfg.color }} />
                </div>

                {/* Content */}
                <div className="ml-3 p-3.5 rounded-xl border border-border/30 bg-muted/10 hover:bg-muted/20 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-foreground">{ev.titolo}</span>
                    <Badge className="text-[8px] border-0 ml-auto" style={{ backgroundColor: `${cfg.color}14`, color: cfg.color }}>{cfg.label}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{ev.dettaglio}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[9px] text-muted-foreground flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{ev.data}</span>
                    <span className="text-[9px] text-muted-foreground flex items-center gap-1"><User className="w-2.5 h-2.5" />{ev.operatore}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ══════ VALIDAZIONE PAI ══════ */
function ValidazionePAI({ pai }) {
  if (!pai) {
    return (
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="pai-section">
        <CardContent className="p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-violet-500" />
            </div>
            <h2 className="text-sm font-semibold text-foreground">PAI — Piano Assistenziale</h2>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <p className="text-xs font-medium text-emerald-700">PAI gia validato e in esecuzione.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="pai-section">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-slate-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">PAI — Piano Assistenziale (Sola Lettura)</h2>
              <p className="text-[10px] text-muted-foreground">Redatto da {pai.redattore} il {pai.dataRedazione}</p>
            </div>
          </div>
          <Badge className="text-[10px] bg-slate-100 text-slate-600 border-0 font-bold">{pai.versione}</Badge>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
          <Eye className="w-3.5 h-3.5 text-blue-500" />
          <p className="text-[11px] text-blue-700 font-medium">Consultazione — Le azioni operative sul PAI sono riservate al Case Manager e al Medico UVMD.</p>
        </div>

        {/* Obiettivi */}
        <div className="p-3.5 rounded-xl bg-muted/20 border border-border/30">
          <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">Obiettivo</p>
          <p className="text-xs font-medium text-foreground">{pai.obiettivi}</p>
        </div>

        {/* Interventi */}
        <div className="rounded-xl border border-border/30 overflow-hidden" data-testid="pai-interventi">
          <div className="grid grid-cols-[1fr_auto_2fr] gap-0 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 px-4 py-2">
            <span>Professionista</span>
            <span>Frequenza</span>
            <span>Prestazione</span>
          </div>
          {pai.interventi.map((int, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_2fr] gap-0 px-4 py-3 border-t border-border/20" data-testid={`pai-int-${i}`}>
              <span className="text-xs font-semibold text-foreground">{int.professionista}</span>
              <Badge variant="outline" className="text-[9px] mx-3 whitespace-nowrap">{int.frequenza}</Badge>
              <span className="text-[11px] text-muted-foreground">{int.prestazione}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
          <span><strong>Durata:</strong> {pai.durata}</span>
          <span><strong>Note:</strong> {pai.note}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/* ══════ MODALE FSE ══════ */
const tipoDocConfig = {
  referto: { icon: FlaskConical, color: '#3B82F6', bg: '#EFF6FF', label: 'Referto' },
  'lettera-dimissione': { icon: Hospital, color: '#EF4444', bg: '#FEF2F2', label: 'Lettera Dimissione' },
  prescrizione: { icon: FileText, color: '#8B5CF6', bg: '#F5F3FF', label: 'Prescrizione' },
  certificato: { icon: ShieldAlert, color: '#F59E0B', bg: '#FFFBEB', label: 'Certificato' },
  pai: { icon: File, color: '#10B981', bg: '#ECFDF5', label: 'PAI' },
};

function FseModal({ patientId, patientName, onClose }) {
  const docs = documentiFSE[patientId] || [];
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="fse-modal-backdrop">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-card rounded-2xl shadow-xl border border-border/40 w-full max-w-2xl max-h-[80vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="fse-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/20">
          <div>
            <h2 className="text-base font-semibold text-foreground">Fascicolo Sanitario Elettronico</h2>
            <p className="text-xs text-muted-foreground">{patientName} — {docs.length} documenti</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted/40 transition-colors" data-testid="fse-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nessun documento disponibile per questo paziente.</p>
          ) : docs.map((doc) => {
            const cfg = tipoDocConfig[doc.tipo] || tipoDocConfig.referto;
            const DocIcon = cfg.icon;
            const isOpen = expanded === doc.id;
            return (
              <div key={doc.id} className="rounded-xl border border-border/30 overflow-hidden" data-testid={`fse-doc-${doc.id}`}>
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/10 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : doc.id)}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bg }}>
                    <DocIcon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">{doc.titolo}</p>
                    <p className="text-[10px] text-muted-foreground">{doc.data} — {doc.struttura}</p>
                  </div>
                  <Badge className="text-[8px] border-0" style={{ backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</Badge>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 pt-0">
                    <div className="p-3 rounded-lg bg-muted/20 text-xs text-muted-foreground">{doc.contenuto}</div>
                    <p className="text-[9px] text-muted-foreground mt-1.5">Medico: {doc.medico}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

/* ══════ PAGINA PRINCIPALE ══════ */
export default function MMGPatientDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const patient = pazientiInCaricoMMG.find(p => p.id === parseInt(id));
  const eventi = diarioClinico[id] || [];
  const pai = paiDaValidare[id] || null;
  const [showFse, setShowFse] = useState(false);

  const [activeTab, setActiveTab] = useState('diario');

  if (!patient) {
    return (
      <AppLayout role="mmg">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-sm text-muted-foreground">Paziente non trovato.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/mmg/pazienti')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Torna alla Lista
          </Button>
        </div>
      </AppLayout>
    );
  }

  const initials = patient.nome.split(' ').map(w => w[0]).join('');

  return (
    <AppLayout role="mmg">
      <div className="space-y-6" data-testid="mmg-patient-dossier">
        {/* Back */}
        <button onClick={() => navigate('/mmg/pazienti')} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group" data-testid="back-to-list">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />Torna alla Lista Pazienti
        </button>

        {/* Patient Header */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="patient-header">
          <CardContent className="p-6 flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-slate-500 dark:text-slate-300">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-foreground" data-testid="patient-name">{patient.nome}</h1>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <Badge variant="outline" className="text-[10px]">{patient.cia}</Badge>
                <span className="text-xs text-muted-foreground">{patient.eta} anni · {patient.cf}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{patient.diagnosi}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="gap-2 rounded-xl h-10"
                onClick={() => setShowFse(true)}
                data-testid="btn-accesso-fse"
              >
                <CloudDownload className="w-4 h-4" />Accesso FSE
              </Button>
              {patient.provider && (
                <div className="text-right hidden sm:block">
                  <p className="text-[10px] text-muted-foreground font-medium">Provider</p>
                  <p className="text-xs font-semibold text-foreground">{patient.provider}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted/30 rounded-2xl border border-border/20 w-fit" data-testid="patient-tabs">
          <button
            onClick={() => setActiveTab('diario')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeTab === 'diario' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-diario"
          >
            <ClipboardCheck className="w-4 h-4" />Diario Clinico Integrato
          </button>
          <button
            onClick={() => setActiveTab('pai')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeTab === 'pai' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-pai"
          >
            <ClipboardCheck className="w-4 h-4" />PAI
          </button>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'diario' ? (
            <motion.div key="diario" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <DiarioTimeline eventi={eventi} />
            </motion.div>
          ) : (
            <motion.div key="pai" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
              <ValidazionePAI
                pai={pai}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* FSE Modal */}
      <AnimatePresence>
        {showFse && (
          <FseModal
            patientId={parseInt(id)}
            patientName={patient.nome}
            onClose={() => setShowFse(false)}
          />
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
